/**
 * Staff review queue for inbound parent/physician PDFs without a routing QR.
 * Usage: /admin/medform-pending
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api, { studentAPI } from '../services/api';

function fmtIso(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

export default function MedformPendingPage() {
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [assignRow, setAssignRow] = useState(null);
  const [studentQuery, setStudentQuery] = useState('');
  const [studentOptions, setStudentOptions] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [title, setTitle] = useState('Parent/physician upload (manual assign)');
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [sessionName, setSessionName] = useState('');

  useEffect(() => {
    api.get('/sessions/current-session/')
      .then((res) => {
        if (res.data?.id) {
          setSessionId(String(res.data.id));
          setSessionName(res.data.name || '');
        }
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/sessions/medform-pending/', { params: { status: statusFilter } });
      setRows(res.data.results || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load pending intake items.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!assignRow) {
      setPreviewUrl('');
      return undefined;
    }
    let objectUrl = '';
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/sessions/medform-pending/${assignRow.id}/file/`, {
          responseType: 'blob',
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setPreviewUrl(objectUrl);
      } catch {
        if (!cancelled) setPreviewUrl('');
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assignRow]);

  useEffect(() => {
    if (!assignRow || !sessionId) {
      setStudentOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await studentAPI.getStudents(sessionId, {
          discharge: 'active',
          page_size: 2000,
        });
        if (cancelled) return;
        const results = res.data.results || res.data || [];
        const all = Array.isArray(results) ? results : [];
        const q = studentQuery.trim().toLowerCase();
        const filtered = q
          ? all.filter((s) => {
              const name = `${s.last_name || ''} ${s.first_name || ''}`.toLowerCase();
              return name.includes(q) || String(s.id).includes(q);
            })
          : all.slice(0, 40);
        setStudentOptions(filtered.slice(0, 40));
      } catch {
        if (!cancelled) setStudentOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignRow, sessionId, studentQuery]);

  const openAssign = (row) => {
    setAssignRow(row);
    setSelectedStudentId('');
    setStudentQuery('');
    setStudentOptions([]);
    setTitle('Parent/physician upload (manual assign)');
  };

  const submitAssign = async () => {
    if (!assignRow || !selectedStudentId) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/sessions/medform-pending/${assignRow.id}/assign/`, {
        student_id: Number(selectedStudentId),
        title,
        category: 'service_records',
      });
      setAssignRow(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Assign failed.');
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async (row) => {
    if (!window.confirm('Dismiss this item without filing it to a student?')) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/sessions/medform-pending/${row.id}/dismiss/`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Dismiss failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        Paper intake — pending assignment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        PDFs emailed to medforms@ or syms@ without a readable routing QR land here. Assign to the
        correct student — do not guess from the sender address.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="assigned">Assigned</MenuItem>
          <MenuItem value="dismissed">Dismissed</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {loading ? (
        <CircularProgress size={28} />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Received</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Site</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No items.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{fmtIso(row.message_received_at || row.created_at)}</TableCell>
                    <TableCell>{row.reason_label || row.reason}</TableCell>
                    <TableCell>{row.site_name || 'Unconfirmed'}</TableCell>
                    <TableCell>
                      {row.status}
                      {row.assigned_student_name ? ` → ${row.assigned_student_name}` : ''}
                    </TableCell>
                    <TableCell align="right">
                      {row.status === 'open' ? (
                        <>
                          <Button size="small" onClick={() => openAssign(row)} disabled={busy}>
                            Assign
                          </Button>
                          <Button size="small" color="inherit" onClick={() => dismiss(row)} disabled={busy}>
                            Dismiss
                          </Button>
                        </>
                      ) : row.assigned_student_id ? (
                        <Link component={RouterLink} to={`/students/${row.assigned_student_id}`}>
                          Open student
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={Boolean(assignRow)} onClose={() => setAssignRow(null)} maxWidth="md" fullWidth>
        <DialogTitle>Assign pending PDF to student</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Item #{assignRow?.id} — {assignRow?.reason_label}. Preview and pick the student carefully.
          </Typography>
          {previewUrl ? (
            <Box
              component="iframe"
              title="Pending PDF"
              src={previewUrl}
              sx={{ width: '100%', height: 360, border: 0, mb: 2 }}
            />
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              PDF preview unavailable — you can still assign.
            </Alert>
          )}
          <TextField
            fullWidth
            size="small"
            label="Search student (active session)"
            value={studentQuery}
            onChange={(e) => setStudentQuery(e.target.value)}
            helperText={
              sessionId
                ? `Searching in session: ${sessionName || sessionId}`
                : 'Select a site in the header so the active session is available.'
            }
            sx={{ mb: 2 }}
          />
          <TextField
            select
            fullWidth
            size="small"
            label="Student"
            value={selectedStudentId}
            onChange={(e) => setSelectedStudentId(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Select…</MenuItem>
            {studentOptions.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.last_name}, {s.first_name}
                {s.date_of_birth ? ` (${s.date_of_birth})` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            size="small"
            label="Document title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignRow(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitAssign}
            disabled={busy || !selectedStudentId}
          >
            File to student
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
